// Single source of truth for site-wide facts, links and sourced figures.
// Rule: every number shown on the site must have a `source` entry here.

export const SITE = {
  name: 'Cardinal Grid',
  kicker: 'Open Initiative',
  tagline: 'Open methods for load forecasting and grid data quality.',
  description:
    'An independent, open initiative publishing reproducible benchmarks, tools and technical notes on load forecasting and telemetry data quality for the U.S. bulk power system.',
  github: 'https://github.com/cardinalgrid',
  email: 'contact@cardinalgrid.com', // project inbox
  maintainer: {
    name: 'Ricardo Guerra',
    email: 'ricardo.guerra@cardinalgrid.com',
    title: 'Electrical Engineer, M.Sc. in Optimization of Energy Systems',
    orcid: 'https://orcid.org/0000-0001-6699-9951',
    scholar: 'https://scholar.google.com/citations?user=_UGcgUcAAAAJ',
    linkedin: 'https://br.linkedin.com/in/guerraricardo',
  },
  licenses: {
    code: 'Apache-2.0',
    notes: 'CC BY 4.0',
  },
};

export type Source = {
  id: string;
  short: string;
  title: string;
  org: string;
  year: string;
  url: string;
};

export const SOURCES: Record<string, Source> = {
  elliott: {
    id: 'elliott',
    short: 'FERC/NERC, Winter Storm Elliott Final Report (2023)',
    title:
      'Inquiry into Bulk-Power System Operations During December 2022 Winter Storm Elliott: Final Report',
    org: 'FERC, NERC and Regional Entity Staff',
    year: '2023',
    url: 'https://www.ferc.gov/news-events/news/ferc-nerc-release-final-report-lessons-winter-storm-elliott',
  },
  arctic2025: {
    id: 'arctic2025',
    short: 'FERC/NERC, January 2025 Arctic Events review (2025)',
    title: 'January 2025 Arctic Events: A System Performance Review',
    org: 'FERC, NERC and Regional Entity Staff',
    year: '2025',
    url: 'https://www.ferc.gov/news-events/news/ferc-nerc-issue-report-system-performance-during-january-2025-arctic-weather',
  },
  ltra2025: {
    id: 'ltra2025',
    short: 'NERC, 2025 Long-Term Reliability Assessment (2026)',
    title: '2025 Long-Term Reliability Assessment',
    org: 'North American Electric Reliability Corporation',
    year: '2026',
    url: 'https://www.nerc.com/globalassets/our-work/assessments/nerc_ltra_2025.pdf',
  },
  doeRA2025: {
    id: 'doeRA2025',
    short: 'DOE, Report on Evaluating U.S. Grid Reliability and Security (2025)',
    title: 'Report on Evaluating U.S. Grid Reliability and Security',
    org: 'U.S. Department of Energy',
    year: '2025',
    url: 'https://www.energy.gov/sites/default/files/2025-07/DOE%20Final%20EO%20Report%20%28FINAL%20JULY%207%29.pdf',
  },
  nercAI: {
    id: 'nercAI',
    short: 'NERC, AI & ML in Real-Time System Operations (2024)',
    title: 'Artificial Intelligence & Machine Learning in Real-Time System Operations (White Paper)',
    org: 'North American Electric Reliability Corporation',
    year: '2024',
    url: 'https://www.nerc.com/pa/rrm/bpsa/Documents/Whitepaper-AI%20and%20ML%20in%20Real-Time%20System%20Operations.pdf',
  },
  doeAI: {
    id: 'doeAI',
    short: 'DOE, AI for Energy (2024)',
    title: 'AI for Energy: Opportunities for a Modern Grid and Clean Energy Economy',
    org: 'U.S. Department of Energy',
    year: '2024',
    url: 'https://www.energy.gov/sites/default/files/2024-04/AI%20EO%20Report%20Section%205.2g%28i%29_043024.pdf',
  },
  lbnl: {
    id: 'lbnl',
    short: 'LBNL-55718, Cost of Power Interruptions (2004)',
    title: 'Understanding the Cost of Power Interruptions to U.S. Electricity Consumers',
    org: 'Lawrence Berkeley National Laboratory',
    year: '2004',
    url: 'https://www.osti.gov/biblio/834270',
  },
  eia930: {
    id: 'eia930',
    short: 'EIA, Hourly Electric Grid Monitor (Form EIA-930)',
    title: 'Hourly Electric Grid Monitor',
    org: 'U.S. Energy Information Administration',
    year: 'ongoing',
    url: 'https://www.eia.gov/electricity/gridmonitor/',
  },
  eo14156: {
    id: 'eo14156',
    short: 'Executive Order 14156, National Energy Emergency (2025)',
    title: 'Declaring a National Energy Emergency',
    org: 'Executive Office of the President',
    year: '2025',
    url: 'https://www.federalregister.gov/documents/2025/01/29/2025-02003/declaring-a-national-energy-emergency',
  },
  sentinelBenchmark: {
    id: 'sentinelBenchmark',
    short: 'grid-data-sentinel v0.5.0 benchmark (results/summary.json)',
    title: 'Grid Data Sentinel v0.5.0: benchmark on EIA-930 hourly demand with injected faults',
    org: 'Cardinal Grid',
    year: '2026',
    url: 'https://github.com/cardinalgrid/grid-data-sentinel/blob/v0.5.0/results/summary.json',
  },
  sentinelStorms: {
    id: 'sentinelStorms',
    short: 'grid-data-sentinel v0.5.0, docs/storms.md',
    title: 'Grid Data Sentinel v0.5.0: detectors on the four winter storms (Uri, Elliott, Gerri and Heather, January 2025)',
    org: 'Cardinal Grid',
    year: '2026',
    url: 'https://github.com/cardinalgrid/grid-data-sentinel/blob/v0.5.0/docs/storms.md',
  },
  sentinelFeederLoss: {
    id: 'sentinelFeederLoss',
    short: 'grid-data-sentinel v0.5.0, results/feeder_loss and CHANGELOG',
    title: 'Grid Data Sentinel v0.5.0: partial loss of load telemetry, benchmark with injected feeder losses',
    org: 'Cardinal Grid',
    year: '2026',
    url: 'https://github.com/cardinalgrid/grid-data-sentinel/blob/v0.5.0/CHANGELOG.md',
  },
  sentinelV05Cases: {
    id: 'sentinelV05Cases',
    short: 'grid-data-sentinel v0.5.0, docs/real_cases_v05.md',
    title: 'What Grid Data Sentinel v0.5 changes on the public EIA-930 record',
    org: 'Cardinal Grid',
    year: '2026',
    url: 'https://github.com/cardinalgrid/grid-data-sentinel/blob/v0.5.0/docs/real_cases_v05.md',
  },
  sentinelRealCases: {
    id: 'sentinelRealCases',
    short: 'grid-data-sentinel, docs/real_cases.md',
    title: 'Real anomalies in the EIA-930 demand record: survey and gallery',
    org: 'Cardinal Grid',
    year: '2026',
    url: 'https://github.com/cardinalgrid/grid-data-sentinel/blob/main/docs/real_cases.md',
  },
  forecasterScoreboard: {
    id: 'forecasterScoreboard',
    short: 'ba-load-forecast 1.0.0, docs/scoreboard_43_bas.md',
    title: 'The recipe against each balancing authority\'s forecast, 43 BAs, 2023 to 2025',
    org: 'Cardinal Grid (open code, public EIA-930 and NOAA data)',
    year: '2026',
    url: 'https://github.com/cardinalgrid/ba-load-forecast/blob/v1.0.0/docs/scoreboard_43_bas.md',
  },
  forecasterOffsets: {
    id: 'forecasterOffsets',
    short: 'ba-load-forecast 1.0.0, docs/df_hourly_bias_43_bas.md',
    title: 'Published day-ahead forecasts with their own hourly offset removed, 43 BAs',
    org: 'Cardinal Grid (open code, public EIA-930 data)',
    year: '2026',
    url: 'https://github.com/cardinalgrid/ba-load-forecast/blob/v1.0.0/docs/df_hourly_bias_43_bas.md',
  },
  forecasterStrata: {
    id: 'forecasterStrata',
    short: 'ba-load-forecast 1.0.0, docs/evaluation_43_bas_strata.md',
    title: 'The recipe against the published forecasts by kind of day, 38 comparable BAs',
    org: 'Cardinal Grid (open code, public EIA-930 and NOAA data)',
    year: '2026',
    url: 'https://github.com/cardinalgrid/ba-load-forecast/blob/v1.0.0/docs/evaluation_43_bas_strata.md',
  },
  forecasterBands: {
    id: 'forecasterBands',
    short: 'ba-load-forecast 1.0.0, docs/note4_43_bas_strata.md',
    title: 'Prediction bands on the 43 BAs, 2023 to 2025, by kind of day',
    org: 'Cardinal Grid (open code, public EIA-930 and NOAA data)',
    year: '2026',
    url: 'https://github.com/cardinalgrid/ba-load-forecast/blob/v1.0.0/docs/note4_43_bas_strata.md',
  },
  forecasterCutoff: {
    id: 'forecasterCutoff',
    short: 'ba-load-forecast 1.0.0, docs/cutoff_stage2.md',
    title: 'How much a later cut-off is worth, eleven development BAs',
    org: 'Cardinal Grid (open code, public EIA-930 and NOAA data)',
    year: '2026',
    url: 'https://github.com/cardinalgrid/ba-load-forecast/blob/v1.0.0/docs/cutoff_stage2.md',
  },
  energies2024: {
    id: 'energies2024',
    short: 'Pimentel, Guerra Filho et al., Energies 2024, 17(24), 6403',
    title:
      'Outlier Detection and Correction in Smart Grid Energy Demand Data Using Sparse Autoencoders',
    org: 'Energies (MDPI)',
    year: '2024',
    url: 'https://www.mdpi.com/1996-1073/17/24/6403',
  },
};

// Figures shown in the stats strip. Each one names its source.
export const FACTS = [
  {
    value: 'up to 11.6%',
    label: 'day-ahead load under-forecast at one balancing authority during Winter Storm Elliott',
    source: SOURCES.elliott,
  },
  {
    value: '+224 GW',
    label: 'projected growth in U.S. summer peak demand over the next ten years',
    source: SOURCES.ltra2025,
  },
  {
    value: '~$80 B / yr',
    label: 'estimated cost of power interruptions to U.S. electricity consumers',
    source: SOURCES.lbnl,
  },
  {
    value: '65',
    label: 'balancing authorities reporting hourly demand and day-ahead forecasts to EIA-930',
    source: SOURCES.eia930,
  },
];
